import React, { useEffect } from "react";
import {
  Form,
  Input,
  Button,
  Select,
  Typography,
  Row,
  Col,
  Divider,
  notification,
} from "antd";
import { BankOutlined, LockOutlined } from "@ant-design/icons";
import useSaveBankDetails from "../../../hooks/landlord/useSaveBankDetails";
import { openError, openSuccess } from "../../../utils/helper/message";

const { Title, Text } = Typography;
const { Option } = Select;

const AddBankAccount = () => {
  const [form] = Form.useForm();
  const { bankDetails, saveBankDetails, loading } = useSaveBankDetails();
  const [api, contextHolder] = notification.useNotification();
  const isDisabled = bankDetails && Object.keys(bankDetails).length > 0;

  useEffect(() => {
    if (bankDetails) {
      form.setFieldsValue(bankDetails);
    }
  }, [bankDetails, form]);

  const onFinish = async (values) => {
    const { confirmAccountNumber, ...payload } = values;
    const result = await saveBankDetails(payload);
    if (result.success) {
      openSuccess(api, "Bank account added successfully!");
      form.resetFields();
    } else {
      openError(api, result.error || "Failed to save bank details");
      console.error("Error saving bank details:", result.error);
    }
  };

  return (
    <Row>
      <Col span={24}>
        <Form
          layout="vertical"
          onFinish={onFinish}
          form={form}
          initialValues={bankDetails || {}}
          disabled={isDisabled}
        >
          {contextHolder}

          <div style={{ maxWidth: 600, padding: 24 }}>
            <div style={{ textAlign: "center", marginBottom: 24 }}>
              <BankOutlined style={{ fontSize: 64, color: "#9701fc" }} />
              <Title level={2} style={{ marginTop: 16 }}>
                Add Bank Account
              </Title>
              <Text>
                Add a bank account to start accepting ACH or credit card
                payments from tenants.
              </Text>
            </div>

            <Title level={5}>Full Name <span style={{ color: "red" }}>*</span></Title>
            <Form.Item
              name="fullName"
              rules={[{ required: true, message: "Full name is required" }]}
            >
              <Input placeholder="Your name as per bank records" />
            </Form.Item>

            <Title level={5}>Account Type <span style={{ color: "red" }}>*</span></Title>
            <Form.Item
              name="accountType"
              rules={[
                { required: true, message: "Please select account type" },
              ]}
            >
              <Select placeholder="Select type">
                <Option value="checking">Checking</Option>
                <Option value="savings">Savings</Option>
              </Select>
            </Form.Item>

            <Title level={5}>Routing Number <span style={{ color: "red" }}>*</span></Title>
            <Form.Item
              type="number"
              name="routingNumber"
              rules={[
                { required: true, message: "Routing number is required" },
                {
                  pattern: /^\d{9}$/,
                  message: "Routing number must be 9 digits",
                },
              ]}
            >
              <Input placeholder="9-digit routing number" />
            </Form.Item>
            
            <Title level={5}>Account Number <span style={{ color: "red" }}>*</span></Title>
            <Form.Item
              name="accountNumber"
              rules={[
                { required: true, message: "Account number is required" },
                {
                  min: 8,
                  message: "Account number must be at least 8 characters",
                },
              ]}
            >
              <Input.Password placeholder="Enter account number" />
            </Form.Item>

            <Title level={5}>Confirm Account Number <span style={{ color: "red" }}>*</span></Title>
            <Form.Item
              name="confirmAccountNumber"
              dependencies={["accountNumber"]}
              rules={[
                {
                  required: true,
                  message: "Please confirm your account number",
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("accountNumber") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject("Account numbers do not match!");
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Re-enter account number" />
            </Form.Item>

            {!bankDetails && (
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={loading}
                  icon={<LockOutlined />}
                >
                  Add Bank Account
                </Button>
              </Form.Item>
            )}

            <Divider />

            <div style={{ textAlign: "center", color: "#666" }}>
              <LockOutlined style={{ fontSize: 20 }} />
              <br />
              <Text strong>Safer Than A Check</Text>
              <p style={{ fontSize: 14 }}>
                Bank details are encrypted and securely transmitted using SSL.
                Rent payments are processed via ACH and usually settle in 3
                business days.
              </p>
            </div>
          </div>
        </Form>
      </Col>
    </Row>
  );
};

export default AddBankAccount;
